import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/common/Modal";
import { FormikHelpers, useFormik } from "formik";
import { DealerInterface, TDealerFilter } from "../../interfaces/dealerInterface";
import { usePagination } from "../../components/common/data-table/hooks/usePagination";
import { ListSearchControls } from "../../components/common/data-table/ListSearchControls";
import { ListViewControls } from "../../components/common/data-table/ListViewControls";
import Button from "../../components/common/Buttons/Button";
import {
  faCheck,
  faEdit,
  faFileInvoiceDollar,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  dealerSchema,
  useDealers,
  useSaveDealer,
  useDealerStatusToggle,
  useExportDealers,
  useDealerByIdQuery,
  DealerForm,
  DealerTable,
  DealerHeader,
} from "../../features/dealers";

const formDataInit: DealerInterface = {
  name: "",
  distributorTypeId: "",
  phoneNumber: "",
  addresses: [],
  invoiceData: {
    taxRegime: "",
    rfc: "",
    businessName: "",
    legalRepresentative: "",
    cfdi: "",
    email: "",
  },
};

const filterInit: TDealerFilter = {};

const DealerPage = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingDealerId, setEditingDealerId] = useState<string | null>(null);
  const { pagination, handlePageChange, handlePageSizeChange, handleSort } =
    usePagination({
      initialPageSize: 10,
      initialSortBy: "dealerId",
      initialSortOrder: "desc",
    });

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<TDealerFilter>(filterInit);

  const handleSearch = () => {
    setFilter({ ...filterInit, search: searchTerm || undefined });
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilter(filterInit);
  };

  const { dealers: _dealers, paginationData, isLoading } = useDealers({
    filter,
    pagination,
  });

  const { data: dealerToEdit } = useDealerByIdQuery(editingDealerId ?? undefined);

  const { handleSave } = useSaveDealer();
  const { handleEnable, handleDisable } = useDealerStatusToggle();
  const { handleExport } = useExportDealers();

  const dealers = useMemo(() => {
    return _dealers.map((item) => {
      const addresses = item?.addresses;
      if (!addresses?.length) return { ...item, state: "", city: "" };

      const fiscalAddress = addresses.find((address) => address.isFiscalAddresses);
      const address = fiscalAddress || addresses[0];
      return {
        ...item,
        state: address.state,
        city: address.city,
      };
    });
  }, [_dealers]);

  const formik = useFormik<DealerInterface>({
    initialValues: dealerToEdit?.data || formDataInit,
    validationSchema: dealerSchema,
    enableReinitialize: true,
    onSubmit: (
      values: DealerInterface,
      formikHelpers: FormikHelpers<DealerInterface>,
    ) => {
      handleSave(values);
      formikHelpers.setSubmitting(false);
      formikHelpers.resetForm({ values: formDataInit });
      handleCloseModal();
    },
  });

  const handleOpenNewDealerModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingDealerId(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingDealerId(null);
    setShowModal(false);
  };

  const handleEditDealer = (dealer: DealerInterface) => {
    const { _id: dealerId } = dealer;
    if (!dealerId) return;
    setEditingDealerId(dealerId);
    setShowModal(true);
  };

  const handleNavigateToSpecialPrice = (dealer: DealerInterface) => {
    navigate(`${dealer._id}/special-prices`);
  };

  const handleNavigateToDistributorTypes = () => {
    navigate("types");
  };

  return (
    <div>
      <DealerHeader
        onExport={handleExport}
        onDistributorTypes={handleNavigateToDistributorTypes}
      />
      <ListSearchControls
        searchLabel="Buscar por nombre"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onSearch={handleSearch}
        onClear={handleClearSearch}
      />
      <ListViewControls
        pageSize={pagination.pageSize}
        onPageSizeChange={handlePageSizeChange}
      >
        <Button size="medium" onClick={handleOpenNewDealerModal} color="primary">
          Nuevo
        </Button>
      </ListViewControls>
      <DealerTable
        data={dealers}
        isLoading={isLoading}
        sortBy={pagination.sortBy as keyof (typeof dealers)[0]}
        sortOrder={pagination.sortOrder}
        onSort={handleSort}
        pagination={{
          currentPage: paginationData?.currentPage ?? 1,
          totalPages: paginationData?.totalPages ?? 0,
          pageSize: paginationData?.pageSize ?? 10,
          total: paginationData?.total ?? 0,
          onPageChange: handlePageChange,
        }}
        extraActions={[
          {
            title: "Editar",
            icon: faEdit,
            actionKey: "edit",
            onClick: handleEditDealer,
          },
          {
            title: "Historial de Precios Especiales",
            actionKey: "distributor-special-price-history",
            icon: faFileInvoiceDollar,
            onClick: handleNavigateToSpecialPrice,
            color: "success",
          },
          {
            title: "Deshabilitar",
            icon: faXmark,
            actionKey: "disable",
            color: "secondary",
            onClick: (row) => handleDisable(row._id!),
            shouldShow: (row) => !!row.active,
          },
          {
            title: "Habilitar",
            icon: faCheck,
            actionKey: "enable",
            color: "success",
            onClick: (row) => handleEnable(row._id!),
            shouldShow: (row) => !row.active,
          },
        ]}
      />
      <Modal show={showModal} close={handleCloseModal} submit={formik.handleSubmit}>
        <DealerForm formik={formik} />
      </Modal>
    </div>
  );
};

export default DealerPage;
